import React, { useContext, useEffect, useState } from "react";
import modalStyles from "./styles/AssetManagement.module.css";
import { UserData } from "../../../Context/DataContext";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { Beneficiarytrustee } from "../Beneficiarytrustee/Beneficiarytrustee";
import PeoplePicker from "./PeoplePicker";

/*
  Who inherits an asset, edited from the Asset Management row.

  The row of avatars with a ✕ on each is the same `Beneficiarytrustee` the Add
  / Edit Asset modal draws. The "+" beside them is this page's own
  `PeoplePicker` — see the note at the top of that file for why the asset
  modal's version could not be reused here.

  It opens as a modal rather than editing inside the cell because the "+"
  opens an absolutely-positioned dropdown, and the table body is a scroll
  container (`overflow-x: auto` on .body_scroll) — anything escaping a cell is
  clipped by it. Ownership and Debt take the same route for the same kind of
  reason.

  Nothing is saved here: the two lists go back to the row's draft and are
  written when the row edit ends, like every other cell.
*/

const FALLBACK = {
  assetManagementText17: "Save",
  assetManagementText18: "Cancel",
  assetManagementText34: "Beneficiaries",
  assetManagementText35: "Trusted Advisors",
  assetManagementText36: "Who this asset goes to",
};

// The caps the Add / Edit Asset modal applies to the same two pickers.
const MAX_BENEFICIARIES = 11;
const MAX_TRUSTEES = 24;

function PeopleModal({ beneficiaries, trustees, onClose, onSave }) {
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;

  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
      FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
        FALLBACK
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];

  const [picked, setPicked] = useState({
    beneficiaries: [...(beneficiaries || [])],
    trustees: [...(trustees || [])],
  });

  /*
    One renderer for both lists: they differ only in which source they read
    names and avatars from, and in how many may be attached.
  */
  const renderGroup = (kind, label, max) => {
    const source = ((userData && userData.UserData) || {})[kind] || [];
    const selected = picked[kind];
    return (
      <div className={modalStyles.people_group}>
        <div className={modalStyles.modal_section}>{label}</div>
        <div className={modalStyles.people_row}>
          {selected.map((user_id) => {
            const person = source.find((p) => p.user_id === user_id);
            // An id with no record behind it has no name to draw initials from.
            if (!person || String(person.name || "").trim() === "") return null;
            return (
              <Beneficiarytrustee
                key={user_id}
                name={[person.name, person.last_name].filter(Boolean).join(" ")}
                priority={person.priority || 0}
                avatar={person.profile_avatar}
                deleatable={true}
                onDelete={() =>
                  setPicked((prev) => ({
                    ...prev,
                    [kind]: prev[kind].filter((id) => id !== user_id),
                  }))
                }
              />
            );
          })}
          {selected.length <= max && (
            <PeoplePicker
              user_type={kind}
              alreadySelected={selected}
              onSelect={(person) =>
                setPicked((prev) =>
                  prev[kind].includes(person.user_id)
                    ? prev
                    : { ...prev, [kind]: [...prev[kind], person.user_id] }
                )
              }
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={modalStyles.modal_backdrop} onClick={onClose}>
      <div
        className={`${modalStyles.modal} ${modalStyles.modal_wide}`}
        role="dialog"
        aria-label={t("assetManagementText36")}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className={`${modalStyles.modal_title} ${modalStyles.modal_title_center}`}
        >
          {t("assetManagementText36")}
        </h3>

        {renderGroup(
          "beneficiaries",
          t("assetManagementText34"),
          MAX_BENEFICIARIES
        )}
        {renderGroup("trustees", t("assetManagementText35"), MAX_TRUSTEES)}

        <div className={modalStyles.modal_actions}>
          <button
            type="button"
            className={modalStyles.modal_cancel}
            onClick={onClose}
          >
            {t("assetManagementText18")}
          </button>
          <button
            type="button"
            className={modalStyles.modal_save}
            onClick={() => onSave(picked)}
          >
            {t("assetManagementText17")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PeopleModal;
